import React from 'react';
import { Grid, FormControlLabel, Checkbox } from "@mui/material";
import TextField from "../../../../../controls/TextField";
import { useFormContext } from "react-hook-form";


export type PriceInput = {
    minAmount: number
    maxAmount: number
    slope:number
    intercept:number
    useSlopeIntercept: boolean
}
type Props = {
    minAmount?: number
    maxAmount?: number
    slope?: number
    intercept?: number
    useSlopeIntercept?: boolean
}

const PriceForm: React.FC<Props> = ({
    minAmount = 0, maxAmount = 100, slope= 0 , intercept=0, useSlopeIntercept = true
}) => {
    const { register, errors, watch, getValues, trigger } = useFormContext();
    const watchUseSlopeIntercept = watch("useSlopeIntercept", useSlopeIntercept);

    return (
        <Grid container alignContent={'flex-start'} justifyContent={'center'} direction={'row'} spacing={2}>
            <Grid item xs={12} sm={5}>
                <TextField defaultValue={minAmount} name={'minAmount'} fullWidth
                    label={'Min Price'}
                    onChange={() => {
                        trigger(['minAmount', 'maxAmount']);
                    }}
                    inputProps={{
                        ref: register({
                            required: 'Required',
                            validate: (v: string) => {
                                const value = Number(v);
                                if (isNaN(value) || value < 0) return 'Not a valid number';
                                const maxRaw = getValues('maxAmount');
                                const max = Number(maxRaw);
                                if (maxRaw !== undefined && maxRaw !== '' && !isNaN(max) && value > max) {
                                    return 'Min Price must be less than or equal to Max Amount';
                                }
                                return true;
                            }
                        }),
                        onChange: () => {
                            trigger(['minAmount', 'maxAmount']);
                        },
                    }}
                    error={Boolean(errors.minAmount)}
                    helperText={errors.minAmount?.message || (errors.minAmount && 'Not a valid number') || ' '} />
            </Grid>
            <Grid item xs={12} sm={5}>
                <TextField defaultValue={maxAmount} name={'maxAmount'} fullWidth
                    label={'Max Amount'}
                    onChange={() => {
                        trigger(['minAmount', 'maxAmount']);
                    }}
                    inputProps={{
                        ref: register({
                            required: 'Required',
                            validate: (v: string) => {
                                const value = Number(v);
                                if (isNaN(value) || value < 0) return 'Not a valid number';
                                const minRaw = getValues('minAmount');
                                const min = Number(minRaw);
                                if (minRaw !== undefined && minRaw !== '' && !isNaN(min) && value < min) {
                                    return 'Max Amount must be greater than or equal to Min Price';
                                }
                                return true;
                            }
                        }),
                        onChange: () => {
                            trigger(['minAmount', 'maxAmount']);
                        },
                    }}
                    error={Boolean(errors.maxAmount)}
                    helperText={errors.maxAmount?.message || (errors.maxAmount && 'Must be greater than or equal to min amount') || ' '} />
            </Grid>

            <Grid item xs={12} sm={10}>
                <FormControlLabel
                    control={
                        <Checkbox 
                            defaultChecked={useSlopeIntercept} 
                            name="useSlopeIntercept" 
                            inputRef={register} 
                        />
                    }
                    label="Use Slope/Intercept Model"
                />
            </Grid>

            {watchUseSlopeIntercept ? (
                <>
                    <Grid item xs={12} sm={5}>
                        <TextField defaultValue={slope} name={'slope'} fullWidth
                            label={'Slope'}
                            inputProps={{ ref: register({ required: 'Required', validate: (v: string) => !Number.isNaN(Number(v)) }) }}
                            error={Boolean(errors.slope)}
                            helperText={errors.slope?.message || (errors.slope && 'Not a valid number') || ' '} />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <TextField defaultValue={intercept} name={'intercept'} fullWidth
                            label={'Intercept'}
                            inputProps={{ ref: register({ required: 'Required', validate: (v: string) => !Number.isNaN(Number(v)) }) }}
                            error={Boolean(errors.intercept)}
                            helperText={errors.intercept?.message || (errors.intercept && 'Not a valid number') || ' '} />
                    </Grid>
                </>
            ) : (
                <>
                    <input type="hidden" name="slope" ref={register} defaultValue={0} />
                    <input type="hidden" name="intercept" ref={register} defaultValue={0} />
                </>
            )}
        </Grid>
    );
}

export default PriceForm;